"use client";

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import type { FC } from "react";
import { Search } from "lucide-react";

import { getStateAbbr } from "../../lib/helpers/stateAbbr";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Button } from "../ui/button";
import type { TSchool } from "../../lib/types";

type Props = {
  data: TSchool[];
  stateSelected?: string | null;
  setStateSelected?: (state: string | null) => void;
};

const SchoolGrid: FC<Props> = ({ data, stateSelected, setStateSelected }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [conferenceFilter, setConferenceFilter] = useState<string | null>(null);

  // Helper function to get local image URL
  const getLocalImageUrl = (school: TSchool) => {
    // Use local image if school has a logoUrl (meaning we should have downloaded it)
    if (school.logoUrl) {
      return `/schools/${school.slug}.jpg`;
    }
    return null;
  };

  // Get unique states and conferences from data
  const states = useMemo(
    () =>
      Array.from(new Set(data.map((row) => row.state)))
        .filter(Boolean)
        .sort(),
    [data]
  );
  const conferences = useMemo(
    () =>
      Array.from(
        new Set(data.map((row) => row.conference).filter(Boolean))
      ).sort(),
    [data]
  );

  // Filter by stateSelected, stateFilter, conferenceFilter, and search term
  const filteredData = useMemo(() => {
    let result = data;
    if (stateSelected) {
      result = result.filter(
        (row) => row.state === getStateAbbr(stateSelected)
      );
    }
    if (stateFilter) {
      result = result.filter((row) => row.state === stateFilter);
    }
    if (conferenceFilter) {
      result = result.filter((row) => row.conference === conferenceFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (row) =>
          row.schoolName.toLowerCase().includes(term) ||
          row.city.toLowerCase().includes(term) ||
          row.state.toLowerCase().includes(term)
      );
    }
    return result;
  }, [data, stateSelected, stateFilter, conferenceFilter, searchTerm]);

  const isFilterActive =
    !!searchTerm || !!stateFilter || !!conferenceFilter || !!stateSelected;

  return (
    <div className="max-w-[1000px] mx-auto py-12">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold ">Let's find your school!</h2>
        <div className="my-6 flex flex-col md:flex-row md:items-center md:justify-center gap-4">
          {/* Search Input */}
          <div className="relative w-full max-w-md mx-auto">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <Search size={20} />
            </span>
            <input
              type="text"
              placeholder="Search for your school"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              aria-label="Search for your school"
              data-testid="school-search-input"
              role="searchbox"
              pattern=".*\S.*"
            />
          </div>
        </div>

        {/* State Filter Pill */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          {/* State Filter using shadcn/ui Select */}
          <Select
            value={stateFilter ?? ""}
            onValueChange={(val) => {
              // Set to null if "All States" is selected
              setStateFilter(val === "__all__" ? null : val || null);
            }}
          >
            <SelectTrigger className="rounded-full border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <SelectValue placeholder="States" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              sideOffset={4}
              className="max-h-[200px] overflow-y-auto"
            >
              <SelectItem value="__all__">State</SelectItem>
              {states
                .filter((state) => !!state && state !== "")
                .map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {/* Conference Filter using shadcn/ui Select */}
          <Select
            value={conferenceFilter ?? ""}
            onValueChange={(val) => {
              // Set to null if "All Conferences" is selected
              setConferenceFilter(val === "__all__" ? null : val || null);
            }}
          >
            <SelectTrigger className="rounded-full border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <SelectValue placeholder="Conference" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              sideOffset={4}
              className="max-h-[200px] overflow-y-auto"
            >
              <SelectItem value="__all__">Conferences</SelectItem>
              {conferences
                .filter((conf) => !!conf && conf !== "")
                .map((conf) => (
                  <SelectItem key={conf} value={conf!}>
                    {conf}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {/* Clear Filters Button */}
          {isFilterActive && (
            <Button
              variant="link"
              type="button"
              onClick={() => {
                setSearchTerm("");
                setStateFilter(null);
                setConferenceFilter(null);
                if (setStateSelected) setStateSelected(null); // Clear stateSelected as well
              }}
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filteredData.length === 0 ? (
          <div className="col-span-2 text-center text-gray-500 py-12 text-lg">
            No data found for the selected filters.
          </div>
        ) : (
          filteredData.map((row, idx) => (
            <Link
              to={`/schools/${row.slug}`}
              key={idx}
              className="flex items-center gap-4 bg-white border-b border-gray-300 px-4 pb-3"
            >
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  {getLocalImageUrl(row) ? (
                    <img
                      src={getLocalImageUrl(row)!}
                      alt={row.schoolName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        // Hide image and show fallback text
                        e.currentTarget.style.display = "none";
                        const fallback = e.currentTarget
                          .nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="w-full h-full flex items-center justify-center text-xl font-bold text-gray-400"
                    style={{ display: getLocalImageUrl(row) ? "none" : "flex" }}
                  >
                    {row.schoolName[0]?.toUpperCase() || "?"}
                  </div>
                </div>
              </div>
              <div className="w-full">
                <div className="font-semibold">{row.schoolName}</div>
                <div className="text-sm text-gray-500">
                  <div>
                    {row.city}, {row.state}
                  </div>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default SchoolGrid;
